import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, buildWhatsAppLink } from "@/lib/api";
import { useI18n, formatDateLocale } from "@/lib/i18n";
import { fireEnhancedConversion } from "@/lib/gtag";

export default function ReserveDateDialog({ open, date, tariff, whatsappNumber, onClose }) {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", preferredDate: "" });
  const [submitting, setSubmitting] = useState(false);

  const isTariffMode = Boolean(tariff);

  const reset = () => setForm({ name: "", email: "", phone: "", message: "", preferredDate: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t.contact.required);
      return;
    }
    setSubmitting(true);
    try {
      const contactPayload = isTariffMode
        ? {
            name: form.name,
            email: form.email,
            phone: form.phone,
            message: `Reserva franja "${tariff.name}" (${tariff.days} · ${tariff.schedule} · ${tariff.price}€). ${form.preferredDate ? `Data preferent: ${form.preferredDate}. ` : ""}${form.message || ""}`.trim(),
            event_date: null,
          }
        : { ...form, event_date: date };
      await api.post("/contact", contactPayload);
      // Google Ads Enhanced Conversions for Leads
      fireEnhancedConversion({
        email: form.email,
        phone: form.phone,
        name: form.name,
        value: isTariffMode && tariff?.price ? Number(tariff.price) : undefined,
      });

      const text = isTariffMode
        ? t.reserve.tariffMessage(form.name, form.email, form.phone, tariff, form.preferredDate, form.message)
        : t.reserve.messageTemplate(form.name, formatDateLocale(date, locale), form.email, form.phone, form.message);
      window.open(buildWhatsAppLink(whatsappNumber, text), "_blank");
      toast.success(t.contact.sent);
      reset();
      onClose();
    } catch {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#fdfcf9] border-[#eae0df] max-w-lg" data-testid="reserve-dialog">
        <DialogHeader>
          <p className="uppercase tracking-[0.28em] text-[10px] text-[#3e664c] mb-1">
            {isTariffMode ? t.reserve.tariffEyebrow : t.reserve.eyebrow}
          </p>
          <DialogTitle className="font-display text-3xl tracking-tight text-[#2A2421]">
            {isTariffMode ? tariff.name : formatDateLocale(date, locale)}
          </DialogTitle>
          <DialogDescription className="text-[#5C544F]">{t.reserve.desc}</DialogDescription>
        </DialogHeader>

        {isTariffMode && (
          <div
            className="rounded-xl border border-[#3e664c]/20 bg-[#3e664c]/5 px-4 py-3 text-sm space-y-1"
            data-testid="reserve-tariff-summary"
          >
            <div className="flex justify-between">
              <span className="text-[#5C544F]">{t.tariffs.colDay}</span>
              <span className="text-[#1c2421] font-medium">{tariff.days}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5C544F]">{t.tariffs.colSchedule}</span>
              <span className="text-[#1c2421] font-medium">{tariff.schedule}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5C544F]">{t.tariffs.colAmount}</span>
              <span className="font-display text-lg text-[#3e664c]">{tariff.price.toFixed(0)}€ <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C544F]/70">+ IVA</span></span>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="r-name" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelName}</Label>
            <Input id="r-name" data-testid="reserve-input-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-white border-[#eae0df]" required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="r-email" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelEmail}</Label>
              <Input id="r-email" type="email" data-testid="reserve-input-email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-white border-[#eae0df]" required />
            </div>
            <div>
              <Label htmlFor="r-phone" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelPhone}</Label>
              <Input id="r-phone" data-testid="reserve-input-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 bg-white border-[#eae0df]" required />
            </div>
          </div>
          {isTariffMode && (
            <div>
              <Label htmlFor="r-prefdate" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.reserve.datePrefLabel}</Label>
              <Input
                id="r-prefdate"
                data-testid="reserve-input-prefdate"
                value={form.preferredDate}
                onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                placeholder={t.reserve.datePrefPlaceholder}
                className="mt-1 bg-white border-[#eae0df]"
              />
            </div>
          )}
          <div>
            <Label htmlFor="r-message" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.reserve.notesLabel}</Label>
            <Textarea id="r-message" data-testid="reserve-input-message" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 bg-white border-[#eae0df]" placeholder={t.reserve.placeholderNotes} rows={3} />
          </div>
          <Button type="submit" data-testid="reserve-submit" disabled={submitting}
            className="w-full rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] py-6 text-sm uppercase tracking-[0.18em]">
            {submitting ? t.contact.submitting : t.reserve.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
